import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { useCreateRib } from "@/api/rib.api";
import {
  CreateRibFormData,
  CreateRibSchema,
} from "@/components/backOffice/parameters/options/rib/rib.form";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";

const FORM_ID = "rib-create-form";

type OptionsRibCreateModalProps = {
  closeModal: () => void;
};

export const OptionsRibCreateModal = ({ closeModal }: OptionsRibCreateModalProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { mutate: createRib, isPending } = useCreateRib();

  const ribForm = useForm<CreateRibFormData>({
    resolver: zodResolver(CreateRibSchema),
    defaultValues: {
      shortenName: "",
      beneficiary: "",
      iban: "",
      bic: "",
      bankName: "",
      bankAddress: "",
    },
  });

  const onSubmit = (data: CreateRibFormData) => {
    if (isPending) return;
    createRib(data, { onSuccess: closeModal });
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={t("ribCreateModal.title")}
      contentClassName="max-w-[600px]"
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button type="submit" form={FORM_ID} disabled={isPending} className="w-full">
            {isPending ? <StyledSpinner /> : t("ribCreateModal.submit")}
          </Button>
          <Button variant="ghost" onClick={closeModal} disabled={isPending} className="w-full">
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={ribForm.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4 p-6"
      >
        <ControlledUnifiedInput
          control={ribForm.control}
          name="shortenName"
          label={t("ribCreateModal.shortenNameLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="beneficiary"
          label={t("ribCreateModal.beneficiaryLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="iban"
          label="IBAN"
          inputType="text"
        />
        <ControlledUnifiedInput control={ribForm.control} name="bic" label="BIC" inputType="text" />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="bankName"
          label={t("ribCreateModal.bankNameLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="bankAddress"
          label={t("ribCreateModal.bankAddressLabel")}
          inputType="text"
        />
      </form>
    </Modal>
  );
};
